
  import { Component, Prop, Watch } from 'vue-property-decorator'
  import { Field } from './field'
  import { mapActions, mapMutations } from 'vuex'
  import { Email, Person } from '@/entities/persons'
  import FieldTitle from '@/components/forms/fields/FieldTitle.vue'

@Component({
  components: {
    FieldTitle,
  },
  methods: {
    ...mapMutations('persons', ['setId', 'setRutInfo', 'setFoundPerson', 'setType', 'setPhoneRequired']),
    ...mapActions('persons', ['getDuplicatesByTextComparison']),
  },
})
  export default class LinkedPerson extends Field {
  @Prop({ type: Object }) readonly properties!: any;
  @Prop({ type: Array, default: () => [] }) readonly items!: Array<any>;
  @Prop({ type: String }) type!: string
  @Prop({ type: Boolean, default: true }) creatable!: boolean
  @Prop({ type: Boolean, default: false }) remove!: boolean
  @Prop({ type: Boolean, default: false }) disabledEdit!: boolean
  @Prop({ type: Boolean, default: false }) phoneRequired!: boolean
  @Prop({ type: Boolean, default: false }) findEmployees!: boolean
  @Prop({ type: String, default: 'Ingresa al menos 5 caractéres para buscar teléfono, rut, email o nombres, si no está registrado, créalo.' }) hint!: string
  setId!: (id: number) => void
  setType!: (payload) => void
  setPhoneRequired!: (payload) => void

  getDuplicatesByTextComparison!: (payload) => Promise<Person[]>
  active = false
  success = ''
  valid = false
  searchLoading = false
  searchTimeout: any = null
  duplicatePersons: Person[] = []
  hasSearched = false
  hasFiveChars = false
  shouldMaintainButton = false
  searchText = ''
  menuOpen = false
  employees: Person[] = []
  isOpeningForm = false
  $refs!: {
    form: HTMLFormElement
  };

  disableSystem = false
  private setRutInfo!: (payload) => void
  private setFoundPerson!: (payload) => void

    get bind () {
    const {
      properties,
      success,
      disabled,
      required,
      fieldRequired,
      multiple,
      chip,
      readonly,
      errorMessages,
    } = this

    return {
      ...properties,
      disabled,
      required,
      multiple,
      errorMessages,
      chip,
      readonly,
      rules: required ? [...fieldRequired] : [],
      name: success ? undefined : properties?.name,
    }
  }

    get shouldShowButton () {
    const { value, hasSearched, creatable, searchLoading } = this

    // Mostrar botón editar si hay valor seleccionado y no está deshabilitado
    if (value?.id && !this.disabledEdit) {
      return true
    }

    // En modo empleados, no mostrar botón de crear (solo editar)
    if (this.findEmployees) {
      return false
    }

    // Mostrar botón + solo DESPUÉS de buscar y cuando:
    // 1. Se ha realizado una búsqueda
    // 2. No hay persona seleccionada
    // 3. Es creatable
    // 4. NO está cargando (esperar a que termine la búsqueda)
    // 5. Y no hay resultados para mostrar
    return creatable &&
           hasSearched &&
           !value?.id &&
           !searchLoading
  }

  get shouldHideDetails () {
    // Ocultar detalles cuando hay resultados de búsqueda activos
    // Esto evita problemas de reposicionamiento del menú
    const hasResults = this.findEmployees ? this.filteredEmployees.length : this.duplicatePersons.length
    return Boolean(!this.isMultiple && hasResults && !this.value?.id && this.searchText?.length)
  }

  get buttonIcon () {
    const { value } = this
    // Si hay valor, mostrar ícono de editar, sino mostrar ícono de crear
    return value?.id ? 'mdi-pencil' : 'mdi-plus-circle-outline'
  }

  handleButtonClick () {
    const { value } = this
    // Marcar que se está abriendo el formulario para evitar interferencia del blur
    this.isOpeningForm = true

    if (value?.id) {
      // Si hay valor, editar la persona
      this.editPerson(value)
    } else {
      // Si no hay valor, abrir formulario para crear
      this.openForm()
    }

    // Resetear la bandera después de un tiempo
    setTimeout(() => {
      this.isOpeningForm = false
    }, 100)
  }

  isValidText (text: any): boolean {
    if (!text) return false
    if (text instanceof Email) {
      if (!text.personal) return false
      return true
    }

    // Verificar que el texto sea válido y no sea undefined, null, o cadenas que representen estos valores
    return text &&
           text !== 'undefined' &&
           text !== 'null' &&
           text !== null &&
           text !== undefined &&
           text?.toString()?.trim() !== ''
  }

  async mounted () {
    if (this.$refs?.form?.resetValidation) this.$refs.form?.resetValidation()
  }

  @Watch('findEmployees', { immediate: true })
  onFindEmployeesChange (newVal) {
    if (newVal) {
      this.loadEmployees()
    }
  }

  // Método para cargar empleados
  async loadEmployees () {
    try {
      this.searchLoading = true
      this.employees = await this.getEmployees()
      // Mostrar todos los empleados inicialmente
      this.duplicatePersons = this.employees
      this.hasSearched = true
    } catch (error) {
      console.error('❌ Error al cargar empleados:', error)
      this.employees = []
      this.duplicatePersons = []
    } finally {
      this.searchLoading = false
    }
  }

  beforeDestroy () {
    // Limpiar timeout al destruir el componente
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
  }

  @Watch('value', { immediate: true, deep: true }) onValue (val) {
    if (val?.id) {
      this.setId(val.id)
      // Si se selecciona una persona válida, resetear shouldMaintainButton
      this.shouldMaintainButton = false
    }

    this.disableSystem = val?.type?.name === 'system'
  }

  @Watch('duplicatePersons', { immediate: true }) onDuplicatePersonsChange (newVal, oldVal) {
    // Controlar la apertura del menú basado en los resultados
    if (!this.isMultiple && !this.value?.id) {
      // En modo empleados, siempre mostrar el menú si hay empleados cargados
      // En modo normal, mostrar solo si hay resultados de búsqueda
      const hasResults = this.findEmployees ? this.employees.length > 0 : (newVal && newVal.length > 0)
      this.menuOpen = hasResults
    }

    // Forzar Vue a recalcular la posición del menú después del cambio
    const resultCount = this.findEmployees ? this.employees.length : (newVal ? newVal.length : 0)
    if (resultCount > 0) {
      this.$nextTick(() => {
        // Forzar reposicionamiento del menú
        this.forceMenuReposition()
      })
    }
  }

  get isMultiple () {
    const { multiple } = this
    return multiple
  }

  // Normalizar valor para modo múltiple
  get normalizedValue () {
    const { multiple, value } = this
    if (!multiple) return value

    // En modo múltiple, asegurar que siempre sea un array
    if (!value) return []
    if (Array.isArray(value)) return value
    return [value] // Convertir objeto único a array
  }

  // Items combinados para el combobox en modo múltiple
  get comboboxItems () {
    const selectedPersons = this.normalizedValue || []
    // Si estamos en modo empleados, usar empleados filtrados; sino, usar duplicatePersons
    const searchResults = this.findEmployees ? this.filteredEmployees : (this.duplicatePersons || [])

    // Usar Map para evitar duplicados por ID
    const personsMap = new Map()

    // Primero agregar personas seleccionadas (prioridad)
    selectedPersons.forEach(person => {
      if (person?.id) {
        personsMap.set(person.id, person)
      }
    })

    // Luego agregar resultados de búsqueda (sin sobreescribir)
    searchResults.forEach(person => {
      if (person?.id && !personsMap.has(person.id)) {
        personsMap.set(person.id, person)
      }
    })

    return Array.from(personsMap.values())
  }

  // Computed para filtrar empleados basado en el texto de búsqueda
  get filteredEmployees () {
    if (!this.findEmployees || !this.employees.length) {
      return []
    }

    if (!this.searchText || this.searchText.length < 2) {
      return this.employees
    }

    const searchTerm = this.searchText.toLowerCase()
    return this.employees.filter(employee => {
      const fullName = employee.fullName?.toLowerCase() || ''
      const uid = employee.uid?.toLowerCase() || ''
      const phone = this.getPhoneDisplay(employee.phone)?.toLowerCase() || ''
      const email = this.getEmailDisplay(employee.email)?.toLowerCase() || ''

      return fullName.includes(searchTerm) ||
             uid.includes(searchTerm) ||
             phone.includes(searchTerm) ||
             email.includes(searchTerm)
    })
  }

  openForm () {
    const { type, phoneRequired } = this
    this.setId(null)
    this.setRutInfo(null)
    this.setFoundPerson(null)
    this.setType(type)
    this.setPhoneRequired(phoneRequired)
    // Resetear el estado del botón al abrir el formulario
    this.shouldMaintainButton = false
    this.$emit('redirect', type)
  }

  editPerson (person) {
    const { type, phoneRequired } = this
    this.setId(person?.id)
    this.setRutInfo(null)
    this.setFoundPerson(null)
    this.setType(type)
    this.setPhoneRequired(phoneRequired)
    // Resetear el estado del botón al editar una persona
    this.shouldMaintainButton = false
    this.$emit('redirect', type)
  }

  async handleSearchInput (searchText?: string) {
    // Actualizar this.searchText cuando viene del v-combobox en modo múltiple
    if (searchText !== undefined) {
      this.searchText = searchText
    }

    // Si se llama desde v-model, usar this.searchText
    const textToSearch = searchText || this.searchText
    // Si estamos en modo empleados, solo filtrar localmente
    if (this.findEmployees) {
      this.handleEmployeeFilter(textToSearch)
      return
    }

    // Si no hay texto de búsqueda, no hacer nada más
    if (!textToSearch || textToSearch.length < 2) {
      // Solo limpiar si realmente no hay búsqueda válida en curso
      if (!this.searchTimeout) {
        this.duplicatePersons = []
        this.hasSearched = false
        this.shouldMaintainButton = false
      }
      return
    }
    this.searchLoading = true
    // Limpiar duplicados solo cuando iniciamos una nueva búsqueda válida
    this.duplicatePersons = []

    // Marcar que se ha realizado una búsqueda
    this.hasSearched = true

    // Limpiar timeout anterior si existe
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    // Establecer nuevo timeout para evitar múltiples llamadas rápidas
    this.searchTimeout = setTimeout(() => this.performSearch(textToSearch), 300)
  }

  // Método para filtrar empleados localmente
  handleEmployeeFilter (searchText: string) {
    // Actualizar duplicatePersons con empleados filtrados para que el template funcione correctamente
    if (!searchText || searchText.length < 2) {
      this.duplicatePersons = this.employees
    } else {
      this.duplicatePersons = this.filteredEmployees
    }
  }

  onBlur () {
    const { value, multiple } = this
    if (multiple && value?.length) {
      this.$emit('input', value.filter(item => item?.id))
    }

    if (!multiple && !value?.id) {
      this.set(undefined)
    }

    // Usar timeout para evitar interferencia con clics en botones
    // especialmente en modo múltiple
    if (multiple) {
      setTimeout(() => {
        // Solo limpiar si no se está procesando una acción o abriendo formulario
        if (!this.searchLoading && !this.isOpeningForm) {
          this.searchText = ''
        }
      }, 200)
    }
  }

  onSearchBlur () {
    // Cerrar la lista con un pequeño delay para permitir clics en los elementos
    // SOLO limpiar searchText si no hay búsqueda activa
    setTimeout(() => {
      if (!this.isMultiple && !this.value?.id && !this.searchLoading) {
        this.menuOpen = false
        this.searchText = ''
      }
    }, 150)
  }

  selectPerson (person: Person) {
    const { multiple } = this

    if (multiple) {
      // En modo múltiple, agregar al array existente
      const currentValue = this.normalizedValue || []
      // Evitar duplicados
      const alreadyExists = currentValue.some(item => item?.id === person?.id)
      if (!alreadyExists) {
        this.$emit('input', [...currentValue, person])
      }
      // En modo múltiple, limpiar búsqueda después de seleccionar
      this.searchText = ''
      this.duplicatePersons = []
      this.hasSearched = false
      this.shouldMaintainButton = false
    } else {
      // En modo single, reemplazar valor
      this.$emit('input', person)
      this.searchText = ''
      this.duplicatePersons = []
      this.hasSearched = false
      this.shouldMaintainButton = false
    }
  }

  // Método específico para seleccionar desde la lista
  selectPersonFromMenu (person: Person) {
    // Cerrar la lista inmediatamente
    this.menuOpen = false
    // Usar la lógica existente de selección
    this.selectPerson(person)
  }

  // Método para manejar selección del empleado desde v-select
  selectEmployee (person: Person) {
    if (person) {
      this.$emit('input', person)
    } else {
      this.$emit('input', undefined)
    }
  }

  // Forzar reposicionamiento del menú
  forceMenuReposition () {
    // Pequeño hack para forzar el recálculo de posición del menú
    if (this.menuOpen) {
      this.menuOpen = false
      this.$nextTick(() => {
        this.menuOpen = true
      })
    }
  }

  handleTextInput (text: string) {
    // Solo procesar si no hay persona seleccionada
    if (!this.value?.id) {
      this.searchText = text
      this.handleSearchInput()
    }
  }

  clearSearchResults () {
    this.duplicatePersons = []
    this.hasSearched = false
    this.shouldMaintainButton = false
  }

  clearAndReset () {
    this.set(undefined)
    this.searchText = ''
    this.menuOpen = false
    this.clearSearchResults()
  }

  onFocus () {
    // Abrir el menú si hay resultados disponibles y no hay valor seleccionado
    if (!this.isMultiple && !this.value?.id) {
      const hasResults = this.findEmployees ? this.employees.length > 0 : this.duplicatePersons.length > 0
      if (hasResults) {
        this.menuOpen = true
      }
    }
  }

  // Manejar input del combobox en modo múltiple
  handleMultipleInput (selectedItems: any) {
    const { multiple } = this

    if (multiple) {
      // En modo múltiple, asegurar que siempre emitamos un array válido
      const validItems = Array.isArray(selectedItems)
        ? selectedItems.filter(item => item?.id)
        : []
      this.$emit('input', validItems)
    } else {
      // En modo single, emitir directamente
      this.$emit('input', selectedItems)
    }
  }

  async performSearch (searchText: string) {
    try {
      // Llamar al endpoint para buscar duplicados
      const duplicates = await this.getDuplicatesByTextComparison({
        searchText,
      })

      // Asignar resultados directamente - Vue se encargará de la reactividad
      this.duplicatePersons = duplicates

      // Configurar botón de mantenimiento
      if (this.duplicatePersons.length === 0) {
        this.shouldMaintainButton = true
      } else {
        this.shouldMaintainButton = false
      }

      this.hasFiveChars = searchText.length > 4
    } catch (error) {
      console.error('❌ Error al buscar personas duplicadas:', error)
      this.duplicatePersons = []
    } finally {
      this.searchLoading = false
    }
  }

  getPhoneDisplay (phone: any): string {
    if (!phone) return 'Invalid phone'

    if (typeof phone === 'object') {
      if (phone.personal) return phone.personal
      if (phone.number) return phone.number
      return 'Invalid phone'
    }

    return phone
  }

  getEmailDisplay (email: any): string {
    if (!email) return ''

    if (typeof email === 'object') {
      return email?.personal || ''
    }

    return email
  }

  async getEmployees () {
    const status = await this.fetchData({
      query: { name: 'find', model: 'ProcessStatus' },
      filter: {
        process: {
          table_name: { _eq: 'employee' },
        },
      },
    })
    const activeStatus = status.find(s => s.status.name === 'active')

   return (await this.fetchData({
      query: { name: 'find', model: 'Person' },
      filter: {
        _and: [
          { user: { roles: { rol: { slug: { _eq: 'staff' } } } } },
          {
            employee: {
            _and: [
              { id_process_status: { _eq: activeStatus.id } },
              { lead_rule: { _contains: { enabledForForwarding: true } } },
            ],
          },
        },
        ],
      },
      force: true,
      limit: 100,
    })).sort((prev, next) => prev.firstName > next.firstName ? 1 : -1)
  }
  }
